import { Button, EmptyState } from '@dev-spendesk/grapes';

import { useModal } from 'common/hooks/useModal';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';

import { HeaderContent } from './HeaderContent';
import { ImportBudgetModalContainer } from '../../../../components/ImportBudgetModal';
import { type BudgetaryExercise } from '../../../../models/budgetaryExercise';
import { type PeriodRange } from '../../../../models/periodRange';
import { sortNamedItems } from '../../../../utils/sortNamedItems';
import { BudgetCard } from '../BudgetCard';
import { BudgetaryExerciseActionsBar } from '../BudgetaryExerciseActionsBar';

export const BudgetaryExercisePageContent = ({
  budgetaryExercise,
  hasBudgets,
  periodRange,
  onRangeChange,
}: {
  budgetaryExercise: BudgetaryExercise;
  hasBudgets: boolean;
  periodRange: PeriodRange;
  onRangeChange: (range: PeriodRange) => void;
}) => {
  const { t, activeLanguage } = useTranslation('global');
  const { isAccountOwner, isController } = useUserRoles();
  const isAccountOwnerOrController = isController || isAccountOwner;
  const canEditBudgets = isAccountOwnerOrController;

  const [
    createBudgetExpenseCategoriesModal,
    showCreateBudgetExpenseCategoriesModal,
    hideCreateBudgetExpenseCategoriesModal,
  ] = useModal(
    ({
      budgetaryExerciseToEdit,
    }: {
      budgetaryExerciseToEdit: BudgetaryExercise;
    }) => {
      if (!budgetaryExerciseToEdit) {
        return <></>;
      }
      return (
        <ImportBudgetModalContainer
          type="create"
          onSuccess={() => hideCreateBudgetExpenseCategoriesModal()}
          budgetaryExerciseId={budgetaryExerciseToEdit.id}
          onExit={() => hideCreateBudgetExpenseCategoriesModal()}
        />
      );
    },
  );

  const { budgets } = budgetaryExercise;

  const configuredBudgets = sortNamedItems(
    budgets.filter((index) => !index.isUnavailable),
    activeLanguage,
  ).map((b) => (
    <BudgetCard
      key={b.id}
      budget={b}
      periodRange={periodRange}
      budgetaryExercise={budgetaryExercise}
    />
  ));

  return (
    <div className="page__container">
      {createBudgetExpenseCategoriesModal}
      <HeaderWithNavigation
        links={[]}
        rightContent={<HeaderContent budgetaryExercise={budgetaryExercise} />}
      >
        {t('navigation.budgets')}
      </HeaderWithNavigation>

      {hasBudgets && (
        <BudgetaryExerciseActionsBar
          onRangeChange={onRangeChange}
          range={periodRange}
          align="left"
          className="p-40 pt-24"
          budgetaryExercise={budgetaryExercise}
          canEditBudgets={canEditBudgets}
        />
      )}
      {!hasBudgets ? (
        <EmptyState
          title={t('budget.redesigned.emptyState.title')}
          subtitle={t('budget.redesigned.emptyState.text')}
          actions={
            <Button
              text={t('budget.redesigned.emptyState.button')}
              onClick={() => {
                showCreateBudgetExpenseCategoriesModal({
                  budgetaryExerciseToEdit: budgetaryExercise,
                });
              }}
            />
          }
          iconName="circle-information"
        />
      ) : null}

      <div className="flex w-full flex-col gap-24 self-center p-40 pt-0">
        {configuredBudgets}
      </div>
    </div>
  );
};
